import {
  ApiError,
  CommercialService,
  EditReviewService,
  EditService,
  TCommercialId,
  TCreateCommercialRequest,
  TCreateEditRequest,
  TUpdateCommercialRequest,
  TUpdateEditRequest,
} from '@talon/web/lib/api';
import { makeObservable, observable, runInAction } from 'mobx';
import { BaseStore } from './baseStore'; // for some dumb reason, these have to be imported individually
import { RootStore } from './rootStore'; // for some dumb reason, these have to be imported individually

export class CommercialStore extends BaseStore {
  isSaving = false;

  constructor(rootStore: RootStore) {
    super(rootStore);
    makeObservable(this, {
      isSaving: observable,
    });
  }

  setIsSaving(isSaving: boolean) {
    runInAction(() => (this.isSaving = isSaving));
  }

  /** Commercials **/

  getCommercial = async (commercialId: TCommercialId) => {
    try {
      const { commercial } = await CommercialService.getCommercial(
        commercialId,
        'sessions,usages,edits,edits.asset,tags,project,usage_estimates,contracts.performer,contracts.role'
      );
      return commercial;
    } catch (e) {
      this.handleError(e);
    }
  };

  listCommercialsByProjectId = async (projectId: string) => {
    try {
      const { commercials } = await CommercialService.listCommercials(
        projectId,
        undefined,
        undefined,
        undefined,
        'edits,edits.asset'
      );
      return commercials;
    } catch (e) {
      this.handleError(e);
    }
  };

  createCommercial = async (newCommercial: TCreateCommercialRequest) => {
    try {
      this.setIsSaving(true);
      const { commercial } = await CommercialService.createCommercial(newCommercial);
      return commercial ? await this.getCommercial(commercial.id) : undefined;
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setIsSaving(false);
    }
  };

  updateCommercial = async (commercialId: TCommercialId, updatedCommercial: TUpdateCommercialRequest) => {
    try {
      this.setIsSaving(true);
      const { commercial } = await CommercialService.updateCommercial(commercialId, updatedCommercial);
      return commercial ? await this.getCommercial(commercial.id) : undefined;
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setIsSaving(false);
    }
  };

  deleteCommercial = async (commercialId: TCommercialId) => {
    try {
      this.setIsSaving(true);
      await CommercialService.deleteCommercial(commercialId);
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setIsSaving(false);
    }
  };

  /** Edits **/

  getEdit = async (editId: string) => {
    try {
      const { edit } = await EditService.getEdit(editId);
      return edit;
    } catch (e) {
      this.handleError(e);
    }
  };

  createEdit = async (edit: TCreateEditRequest) => {
    try {
      this.setIsSaving(true);
      const { edit: newEdit } = await EditService.createEdit(edit);
      return newEdit;
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setIsSaving(false);
    }
  };

  updateEdit = async (editId: string, edit: TUpdateEditRequest) => {
    try {
      this.setIsSaving(true);
      const { edit: updatedEdit } = await EditService.updateEdit(editId, edit);
      return updatedEdit;
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setIsSaving(false);
    }
  };

  deleteEdit = async (editId: string) => {
    try {
      this.setIsSaving(true);
      await EditService.deleteEdit(editId);
      return true;
    } catch (e) {
      this.handleError(e);
      return false;
    } finally {
      this.setIsSaving(false);
    }
  };

  /**
   * Edit Reviews
   */
  createEditReview = async (editId: string) => {
    try {
      const response = await EditReviewService.createEditReview({ editId });
      return response;
    } catch (e) {
      this.handleError(e);
    }
  };

  getEditReviewByEditId = async (editId: string, relations?: string) => {
    try {
      const response = await EditReviewService.getEditReviewByEditId(editId, relations);
      return response;
    } catch (e: unknown) {
      if ((e as ApiError)?.status === 404) {
        return { editReview: undefined };
      }
      this.handleError(e);
    }
  };
}
